import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import style from './header.scss?inline';

@customElement('lit-header')
export class LitNavigation extends LitElement {
    // Properties
    @property({ type: Boolean }) menuOpen = false;
    @property({ type: String }) logoLink = '';
    @property({ type: String }) logoImage = '';
    @property({
        attribute: 'metanavigation',
        converter: {
            fromAttribute: (value: string | null) => {
                if (!value) return [];
                try { return JSON.parse(value); } catch { return []; }
            }
        }
    }) metaNavigation: { href: string; label: string; target?: string }[] = [];
    @property({
        attribute: 'mainnavigation',
        converter: {
            fromAttribute: (value: string | null) => {
                if (!value) return [];
                try { return JSON.parse(value); } catch { return []; }
            }
        }
    }) mainNavigation: { href: string; label: string; target?: string }[] = [];

    // Import styles
    static styles = [unsafeCSS(style)];

    // Toggle the menu open/close state
    private toggleMenu(): void { this.menuOpen = !this.menuOpen; }
    private closeMenu(): void { this.menuOpen = false; }

    // ESC closes menu (global)
    private _onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') this.closeMenu();
    };

    connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('keydown', this._onKeyDown);
    }
    disconnectedCallback(): void {
        window.removeEventListener('keydown', this._onKeyDown);
        super.disconnectedCallback();
    }

    protected updated(changed: Map<string, unknown>): void {
        if (changed.has('menuOpen') && this.menuOpen) {
            this.renderRoot.querySelector<HTMLElement>('#main-drawer')?.focus();
        }
    }

    // Render method
    protected render(): unknown {
        return html`
      <header class="header">
        <div class="header__inner">
          <div class="header__logo">
            <a class="header__logo-link" href="${this.logoLink}">
              <img class="header__logo-img" src="${this.logoImage}" alt="Logo" />
            </a>
          </div>

            <nav class="header__main-nav" aria-label="Main navigation">
              <ul>
                ${this.mainNavigation.map(i => html`
                  <li>
                    <a href=${i.href} target=${i.target || '_self'}>${i.label}</a>
                  </li>
                `)}
              </ul>
            </nav>

          <nav class="header__meta-nav" aria-label="Meta navigation">
            <ul>
              ${this.metaNavigation.map(i => html`
                <li>
                  <a href=${i.href} target=${i.target || '_self'}>${i.label}</a>
                </li>
              `)}
            </ul>
          </nav>

          <div class="header__burger">
            <button
              class="header__burger-button ${this.menuOpen ? 'header__burger-button--open' : ''}"
              @click=${this.toggleMenu}
              aria-label="Menü öffnen"
              aria-expanded=${String(this.menuOpen)}
              aria-controls="main-drawer"
            >
              <span class="header__burger-icon" aria-hidden="true">
                <span class="header__burger-bar"></span>
                <span class="header__burger-bar"></span>
                <span class="header__burger-bar"></span>
              </span>
            </button>

            <div
              id="main-drawer"
              class="header__drawer ${this.menuOpen ? 'header__drawer--open' : ''}"
              tabindex="-1"
              role="navigation"
              aria-label="Hauptnavigation"
            >
              <div class="header__drawer-header">
                <div class="header__drawer-logo">
                  <a class="header__logo-link" href="${this.logoLink}">
                    <img class="header__logo-img" src="${this.logoImage}" alt="Logo" />
                  </a>
                </div>
                <button class="header__drawer-close" aria-label="Menü schließen" @click=${this.closeMenu}>
                  <span class="header__close-icon" aria-hidden="true">
                    <span class="header__close-bar"></span>
                    <span class="header__close-bar"></span>
                  </span>
                </button>
              </div>

              <nav class="header__main-nav header__main-nav--mobile" aria-label="Main navigation">
                <ul>
                  ${this.mainNavigation.map(i => html`
                    <li>
                      <a href=${i.href} target=${i.target || '_self'}>${i.label}</a>
                    </li>
                  `)}
                </ul>
              </nav>

              <nav class="header__meta-nav header__meta-nav--mobile" aria-label="Meta navigation mobile">
                <ul>
                  ${this.metaNavigation.map(i => html`
                    <li>
                      <a href=${i.href} target=${i.target || '_self'}>${i.label}</a>
                    </li>
                  `)}
                </ul>
              </nav>
            </div>

            <div
              class="header__backdrop ${this.menuOpen ? 'header__backdrop--open' : ''}"
              @click=${this.closeMenu}
              aria-hidden="true"
            ></div>
          </div>
        </div>
      </header>
    `;
    }
}
